<script lang="ts">
import type { ClassValue } from 'svelte/elements';

import { Floating, type FloatingPlacement } from '../floating';
import { popupEnterParent } from '../floating/popup-enter';
import { uniqueId } from '../unique-id';
import ContextMenu from './context-menu.svelte';

export let isOpen: boolean;
export let label: string | undefined = undefined;
export let describedBy: string | undefined = undefined;
export let placement: FloatingPlacement = 'bottom-start';
export let offset = 0;
export let buttonCX: ClassValue = '';
export let menuCX: ClassValue = '';
export let onChange: (isOpen: boolean) => unknown;
/** Keep the menu anchored to its button as ancestors scroll or resize. */
export let auto = false;

const buttonID = uniqueId('floating-menu-control');
const menuID = uniqueId('floating-menu');
const openMenu = () => onChange(true);
const closeMenu = () => onChange(false);

let referenceElement: HTMLElement | undefined;

const handleClickOutside = (element: Element) => {
  if (!referenceElement?.contains(element)) {
    closeMenu();
  }
};

const handleEscape = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    event.preventDefault();
    closeMenu();
  }
};
</script>

<svelte:window on:keydown={isOpen ? handleEscape : undefined} />

<button
  type="button"
  id={buttonID}
  class={buttonCX}
  aria-haspopup="menu"
  aria-controls={menuID}
  aria-expanded={isOpen}
  aria-label={label}
  aria-describedby={describedBy}
  on:click={isOpen ? closeMenu : openMenu}
  bind:this={referenceElement}
>
  <slot name="control" />
</button>

{#if isOpen}
  <Floating
    {auto}
    {offset}
    {placement}
    {referenceElement}
    onClickOutside={handleClickOutside}
  >
    <div
      style="display: contents"
      use:popupEnterParent={() => referenceElement}
    ></div>
    <ContextMenu
      id={menuID}
      labelledBy={buttonID}
      cx={menuCX}
    >
      <slot name="items" />
    </ContextMenu>
  </Floating>
{/if}
